import React, { useState, FC } from "react";

import {
  useSearchManagersSharedInsightPosts,
  useRightSidebarState,
  useApisMembersInsightPostsInsightPostCommentsIndex,
  useApisManagersInsightPostsNew,
  useApiPolling,
  useKnowledgeSlideNavigation,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import {
  RightSidebarToggleIconWithLabel,
  InfiniteScroll,
  FilterIconWithSortMenu,
} from "~/components/molecules";
import {
  InsightPostSearchForm,
  InsightPostCommentsWithInsightPost,
  InsightPostDetailWithEditForm,
} from "~/components/organisms";

import { ManagerRole, MemberRole } from "~/domains";

export const ManagersKnowledgeInsightPostsFromTeamsIndex: FC = () => {
  const [selectGetCommentInsightPostId, setSelectGetCommentInsightPostId] =
    useState<string>("");
  const {
    isSearchOpen,
    isReplyOpen,
    searchOpenToggle,
    setReplyOpen,
    setClose,
    isOpen,
  } = useRightSidebarState("isClose");
  const { navigations } = useKnowledgeSlideNavigation({
    url: "/managers/knowledge_post",
    selectedRole: MemberRole,
  });

  const { data: insightNewData } = useApisManagersInsightPostsNew();
  const { data: insightPostCommentsIndexData, refetch: commentsRefetch } =
    useApisMembersInsightPostsInsightPostCommentsIndex({
      insightPostId: selectGetCommentInsightPostId,
      config: {
        enabled: Boolean(selectGetCommentInsightPostId),
      },
    });
  useApiPolling({
    enabled: isReplyOpen && Boolean(selectGetCommentInsightPostId),
    interval: 1000,
    pollingFunction: commentsRefetch,
    timeoutDuration: null,
  });

  const {
    insightPosts,
    keyword,
    hasNextPage,
    startDate,
    endDate,
    sort,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    selectableDropdownHasCommentIds,
    selectedDropdownHasCommentId,
    isLoading,
    selectableJobCategories,
    selectedJobCategories,
    optionSelectableJobCategoryPhases,
    optionSelectedJobCategoryPhases,
    selectableDropdownEmployees,
    selectedDropdownEmployees,
    selectableDropdownDivisions,
    selectedDropdownDivisions,
    selectableDropdownSections,
    selectedDropdownSections,
    selectableDropdownArchivedEmployees,
    selectedDropdownArchivedEmployees,
    onDivisionChange,
    onSectionChange,
    onEmployeesChange,
    onArchivedEmployeesChange,
    fetchNextPage,
    setSort,
    updateIndexInsightPost,
    deleteIndexInsightPost,
    onChangeDropdownHasBookmarkId,
    onChangeDropdownHasCommentId,
    onChangeJobCategoryPhases,
    onChangeJobCategories,
    onChangeDateRange,
    onChangeKeyword,
    onSearchSubmit,
    onConditionReset,
  } = useSearchManagersSharedInsightPosts();

  const breadCrumbItems = [
    {
      label: "ナレッジシェア",
      href: `/managers/knowledge_post/${ManagerRole.type}`,
    },
    {
      label: "チーム",
      href: `/managers/knowledge_post/${MemberRole.type}`,
    },
  ];

  const openComment = (insightPostId: string) => {
    setSelectGetCommentInsightPostId(insightPostId);
    setReplyOpen();
  };

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent navigations={navigations} withRightSidebar>
        <FilterIconWithSortMenu
          likeSort={() => setSort("likeCountDesc")}
          bookmarkSort={() => setSort("bookmarkCountDesc")}
          createdAtAscSort={() => setSort("createdAtAsc")}
          createdAtDescSort={() => setSort("createdAtDesc")}
          currentSortType={sort}
          className="mt-6"
        />
        <InfiniteScroll
          itemsLength={insightPosts.length}
          nextFetchFunction={fetchNextPage}
          hasMore={hasNextPage}
          isLoading={isLoading}
          className="border-t-2 border-secondary-200"
          noContentMessage="ナレッジシェアがありません"
        >
          {insightPosts.map((post, index) => (
            <InsightPostDetailWithEditForm
              key={post.id}
              insightPost={post}
              openComment={openComment}
              mentions={insightNewData?.mentionableEmployees || []}
              selectableJobCategoryPhases={
                insightNewData?.selectableJobCategoryPhases || []
              }
              updateItem={(newItem) => updateIndexInsightPost(newItem, index)}
              deleteItem={() => {
                deleteIndexInsightPost(index);
                if (selectGetCommentInsightPostId === post.id) {
                  setSelectGetCommentInsightPostId("");
                  setClose();
                }
              }}
            />
          ))}
        </InfiniteScroll>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar>
        <RightSidebarToggleIconWithLabel
          iconOnClick={searchOpenToggle}
          handleOnClose={setClose}
          label={isSearchOpen ? "検索条件" : "リプライ"}
          isOpen={isOpen}
        >
          {isSearchOpen && (
            <InsightPostSearchForm
              onSubmit={onSearchSubmit}
              onConditionReset={onConditionReset}
              date={{
                startDate: startDate,
                endDate: endDate,
                onChange: onChangeDateRange,
              }}
              divisionAndSections={{
                selectableDivisions: selectableDropdownDivisions,
                selectedDivisions: selectedDropdownDivisions,
                selectableSections: selectableDropdownSections,
                selectedSection: selectedDropdownSections,
                selectableEmployees: selectableDropdownEmployees,
                selectedEmployees: selectedDropdownEmployees,
                selectableDropdownArchivedEmployees,
                selectedDropdownArchivedEmployees,
                onDivisionChange,
                onSectionChange,
                onEmployeesChange,
                onArchivedEmployeesChange,
              }}
              searchKeyword={{
                value: keyword,
                onChange: onChangeKeyword,
              }}
              jobCategoryAndJobCategoryPhase={{
                selectableJobCategories: selectableJobCategories,
                selectedJobCategories: selectedJobCategories,
                selectableJobCategoryPhases: optionSelectableJobCategoryPhases,
                selectedJobCategoryPhases: optionSelectedJobCategoryPhases,
                onJobCategoryChange: onChangeJobCategories,
                onJobCategoryPhaseChange: onChangeJobCategoryPhases,
              }}
              hasComment={{
                options: selectableDropdownHasCommentIds,
                value: selectedDropdownHasCommentId,
                onChange: onChangeDropdownHasCommentId,
              }}
              hasBookmark={{
                options: selectableDropdownHasBookmarkIds,
                value: selectedDropdownHasBookmarkId,
                onChange: onChangeDropdownHasBookmarkId,
              }}
            />
          )}
          {isReplyOpen && insightPostCommentsIndexData && (
            <InsightPostCommentsWithInsightPost
              className="mt-6"
              insightPost={insightPostCommentsIndexData.insightPost}
              mentions={insightNewData?.mentionableEmployees || []}
              insightPostComments={
                insightPostCommentsIndexData.insightPostComments || []
              }
              refetch={commentsRefetch}
            />
          )}
        </RightSidebarToggleIconWithLabel>
      </EmployeeLayoutRightSidebar>
    </EmployeeLayout>
  );
};
